<template>
  <div class="user-management-container">
    <div class="page-header">
      <h2 class="page-title">用户管理</h2>
      <p class="page-subtitle">管理商城的所有用户账户、角色和状态。</p>
    </div>
    <div class="table-card">
      <div v-if="isLoading" class="loading-state">
        <div class="spinner"></div>
        <span>正在加载用户数据...</span>
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>用户名</th>
            <th>昵称</th>
            <th>角色</th>
            <th>状态</th>
            <th>注册时间</th>
            <th class="actions-header">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td class="user-info">
              <img :src="user.avatarUrl || 'https://placehold.co/40x40'" alt="avatar" class="avatar">
              <span>{{ user.username }}</span>
            </td>
            <td>{{ user.nickname || '-' }}</td>
            <td>
              <span :class="['tag', user.role.toLowerCase()]">{{ roleMap[user.role] || user.role }}</span>
            </td>
            <td>
              <span :class="['tag', user.status.toLowerCase()]">{{ statusMap[user.status] || user.status }}</span>
            </td>
            <td>{{ new Date(user.createdAt).toLocaleDateString() }}</td>
            <td class="actions-cell">
<template v-if="currentUser && user.id !== currentUser.id">
                  <button v-if="user.status === 'ACTIVE'" @click="toggleBan(user, 'BANNED')" class="action-btn ban" title="封禁用户">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                </button>
                <button v-else @click="toggleBan(user, 'ACTIVE')" class="action-btn unban" title="解封用户">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                </button>
                
                <button v-if="user.role === 'USER'" @click="toggleAdmin(user, 'ADMIN')" class="action-btn promote" title="设为管理员">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path></svg>
                </button>
                <button v-else-if="user.role === 'ADMIN'" @click="toggleAdmin(user, 'USER')" class="action-btn demote" title="取消管理员">
                   <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                </button>

                <button @click="deleteUser(user)" class="action-btn delete" title="删除用户">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                </button>
              </template>
              <span v-else class="self-tag">--</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed } from 'vue';
import { useStore } from 'vuex';

const { appContext } = getCurrentInstance();
const $axios = appContext.config.globalProperties.$axios;
const $notify = appContext.config.globalProperties.$notify;
const $confirm = appContext.config.globalProperties.$confirm;
const store = useStore();

const users = ref([]);
const isLoading = ref(true);
const currentUser = computed(() => store.getters.currentUser);

// 【新增】用于将英文状态和角色映射为中文
const roleMap = {
  USER: '普通用户',
  ADMIN: '管理员',
  SUPER_ADMIN: '站长'
};
const statusMap = {
  ACTIVE: '正常',
  BANNED: '已封禁'
};

const fetchUsers = async () => {
  isLoading.value = true;
  try {
    const res = await $axios.get('/admin/users');
    // 在获取数据时，补充一个 avatarUrl 字段用于显示
// 正确的代码
users.value = res.data.data.list.map(u => ({...u, avatarUrl: u.avatarUrl || `https://i.pravatar.cc/40?u=${u.username}`}));  } catch (error) {
    console.error("获取用户列表失败:", error);
    $notify.add('获取用户列表失败', 'error');
  } finally {
    isLoading.value = false;
  }
};

const toggleBan = (user, newStatus) => {
  const actionText = newStatus === 'BANNED' ? '封禁' : '解封';
  $confirm.show({
    message: `确定要${actionText}用户 "${user.username}" 吗？`,
    onConfirm: async () => {
      try {
        await $axios.put(`/admin/users/${user.id}/status`, { status: newStatus });
        $notify.add(`${actionText}成功`, 'success');
        await fetchUsers();
      } catch (error) {
        $notify.add(error.response?.data?.msg || `${actionText}失败`, 'error');
      }
    }
  });
};

const toggleAdmin = (user, newRole) => {
  const actionText = newRole === 'ADMIN' ? '提升' : '取消';
  $confirm.show({
    message: `确定要${actionText}用户 "${user.username}" 的管理员身份吗？`,
    onConfirm: async () => {
      try {
        await $axios.put(`/admin/users/${user.id}/role`, { role: newRole });
        $notify.add(`${actionText}成功`, 'success');
        await fetchUsers();
      } catch (error) {
        $notify.add(error.response?.data?.msg || `${actionText}失败`, 'error');
      }
    }
  });
};

const deleteUser = (user) => {
  $confirm.show({
    message: `【高危操作】确定要永久删除用户 "${user.username}" 吗？此操作不可恢复。`,
    onConfirm: async () => {
      try {
        await $axios.delete(`/admin/users/${user.id}`);
        $notify.add('删除成功', 'success');
        await fetchUsers();
      } catch (error) {
        $notify.add(error.response?.data?.msg || '删除失败', 'error');
      }
    }
  });
};

onMounted(fetchUsers);
</script>

<style scoped>
.page-header {
  margin-bottom: 25px;
}
.page-title {
  font-size: 1.8em;
  font-weight: 600;
  color: #333;
}
.page-subtitle {
  color: #888;
  margin-top: 5px;
}
.table-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 4px 25px rgba(0,0,0,0.07);
  overflow-x: auto; /* 保证在小屏幕上可以横向滚动 */
}
table {
  width: 100%;
  border-collapse: collapse;
  min-width: 800px; /* 保证表格在窄屏幕下不会过度压缩 */
}
th, td {
  padding: 16px;
  text-align: left;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
thead {
  background-color: #f9fafb;
}
th {
  font-weight: 500;
  color: #888;
  font-size: 14px;
}
tbody tr:hover {
  background-color: #f9fafb;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 10px;
}
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}
.tag {
  padding: 5px 12px;
  border-radius: 15px;
  font-weight: 500;
  font-size: 13px;
  text-align: center;
  display: inline-block;
}
.tag.user { background-color: #e9ecef; color: #495057; }
.tag.admin { background-color: #fff0e6; color: #fa8c16; }
.tag.super_admin { background-color: #fff1f0; color: #f5222d; }
.tag.active { background-color: #f6ffed; color: #52c41a; }
.tag.banned { background-color: #e9ecef; color: #adb5bd; }

.actions-header {
  text-align: center;
}
.actions-cell {
  text-align: center;
  display: flex;
  gap: 8px;
  justify-content: center;
}
.action-btn {
  border: none;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #f1f3f5;
  color: #888;
}
.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}
.action-btn.ban:hover { background-color: #fff1f0; color: #f5222d; }
.action-btn.unban:hover { background-color: #f6ffed; color: #52c41a; }
.action-btn.promote:hover { background-color: #fffbe6; color: #faad14; }
.action-btn.demote:hover { background-color: #e9ecef; color: #495057; }
.action-btn.delete:hover { background-color: #f5222d; color: #fff; }

.self-tag {
  color: #aaa;
  font-style: italic;
  font-size: 13px;
}
.loading-state {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 50px;
  gap: 10px;
  color: #888;
}
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #5e72e4;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>